// 管理者: 学習データ S3 アップロード(設計書 §3.5.7)
//
// API は presigned URL のみ発行し、実体はブラウザから S3 へ直接 PUT する。
// dev では StubS3PresignedUrlIssuer が `*.s3.example.invalid` を返すため
// PUT は確実に失敗する(ダミー URL)。本番で AWS S3 に向けるとそのまま動く。

import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '../client';

export interface IssueUploadUrlInput {
  performer_ulid: string;
  filename: string;
  size_bytes: number;
  content_type?: string;
}

export interface UploadUrlResult {
  upload_url: string;
  expires_at: string;
  method: 'PUT';
  headers: Record<string, string>;
  s3_path: string;
}

export function useIssueTrainingDataUploadUrl() {
  return useMutation({
    mutationFn: (input: IssueUploadUrlInput) =>
      apiRequest<UploadUrlResult>('/admin/training-data/upload-url', {
        method: 'POST',
        body: input,
      }),
  });
}

// ブラウザ → S3 への PUT。XMLHttpRequest を使うのは onProgress を取るため。
export interface PutFileOptions {
  url: string;
  headers: Record<string, string>;
  file: File;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

export interface PutFileResult {
  status: number;
  etag?: string;
  text?: string;
}

export function putFileToS3(opts: PutFileOptions): Promise<PutFileResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', opts.url, true);
    for (const [k, v] of Object.entries(opts.headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (ev) => {
      if (opts.onProgress && ev.lengthComputable) {
        opts.onProgress(ev.loaded, ev.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({
          status: xhr.status,
          etag: xhr.getResponseHeader('ETag') ?? undefined,
        });
      } else {
        reject(new Error(`S3 PUT failed: HTTP ${xhr.status} ${xhr.statusText}\n${xhr.responseText}`));
      }
    };
    xhr.onerror = () => {
      reject(new Error(`S3 PUT ネットワークエラー(${opts.url})`));
    };
    xhr.onabort = () => reject(new Error('upload aborted'));
    if (opts.signal) {
      opts.signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.send(opts.file);
  });
}

// ホスト名で「stub URL かどうか」を判定して、dev では PUT をスキップして
// 「URL 発行のみ成功」表示にできるようにする。
export function isStubUploadUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname.endsWith('.s3.example.invalid');
  } catch {
    return false;
  }
}
